import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { VialImage } from "./VialImage";

// Tag color mapping
const TAG_COLORS = {
  "Weight Loss": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Metabolic Health": "bg-amber-100 text-amber-700 border-amber-200",
  "Physique": "bg-blue-100 text-blue-700 border-blue-200",
  "Recovery": "bg-rose-100 text-rose-700 border-rose-200",
  "Sleep & Hormones": "bg-violet-100 text-violet-700 border-violet-200",
  "Energy & Endurance": "bg-orange-100 text-orange-700 border-orange-200",
  "Longevity": "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Skin & Hair": "bg-pink-100 text-pink-700 border-pink-200",
};

// Tag translation keys mapping
const TAG_KEYS = {
  "Weight Loss": "weightLoss",
  "Metabolic Health": "metabolicHealth",
  "Physique": "physique",
  "Recovery": "recovery",
  "Sleep & Hormones": "sleepHormones",
  "Energy & Endurance": "energyEndurance",
  "Longevity": "longevity",
  "Skin & Hair": "skinHair",
};

export const PeptideCard = ({ peptide }) => {
  const { t } = useTranslation();
  const { name, label, tags, translationKey } = peptide;

  // Get translated name, description, benefits, and dosage
  const translatedName = translationKey ? t(`learn.peptideData.${translationKey}.name`) : name;
  const description = translationKey ? t(`learn.peptideData.${translationKey}.description`) : '';
  const benefits = translationKey ? t(`learn.peptideData.${translationKey}.benefits`) : '';
  const dosage = translationKey ? t(`learn.peptideData.${translationKey}.dosage`) : '';

  // Get translated tag name
  const getTagName = (tag) => {
    const key = TAG_KEYS[tag];
    return key ? t(`learn.tags.${key}`) : tag;
  };

  return (
    <Card className="overflow-hidden border border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
      <div className="flex flex-col md:flex-row">
        {/* Vial Image Section */}
        <div className="md:w-40 lg:w-48 flex-shrink-0 bg-gradient-to-br from-muted/30 to-muted/10 p-6 flex items-center justify-center">
          <VialImage label={label} className="w-24 h-36 md:w-28 md:h-40" />
        </div>

        {/* Content Section */}
        <div className="flex-1 p-5 md:p-6">
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-xl font-bold text-foreground mb-2">{translatedName}</h3>
            <div className="flex flex-wrap gap-1.5">
              {tags.map((tag, index) => (
                <Badge
                  key={index}
                  variant="outline"
                  className={`text-xs font-medium ${TAG_COLORS[tag] || "bg-gray-100 text-gray-700 border-gray-200"}`}
                >
                  {getTagName(tag)}
                </Badge>
              ))}
            </div>
          </div>

          {/* Description Grid */}
          <div className="space-y-3 text-sm">
            {/* What Is */}
            <div>
              <span className="font-semibold text-primary">{t('learn.whatIsIt')}</span>
              <p className="text-muted-foreground mt-0.5 leading-relaxed">
                {description}
              </p>
            </div>

            {/* Benefits */}
            <div>
              <span className="font-semibold text-primary">{t('learn.benefits')}</span>
              <p className="text-muted-foreground mt-0.5 leading-relaxed">
                {benefits}
              </p>
            </div>

            {/* Dosage */}
            <div>
              <span className="font-semibold text-primary">{t('learn.commonDosage')}</span>
              {typeof dosage === "string" ? (
                <p className="text-muted-foreground mt-0.5 leading-relaxed">
                  {dosage}
                </p>
              ) : (
                <ul className="text-muted-foreground mt-0.5 ml-4 list-disc space-y-0.5">
                  {dosage.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PeptideCard;
